import React, { useEffect, useState } from 'react';
import Router from 'next/router';
import { filterValuesSeparator } from 'shared-helpers/consts';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import { EmptyIndicator, MoreOptions } from 'components/common';
import { Box, BoxRow } from 'components/common/box';
import {
  PathStepsAddOrEdit,
  PathStepsOrder,
  PathStepsRemove,
  PathStepSummary
} from '.';

import type { GetPathStepsMinimalRes } from 'shared-types/responses';

const PathSteps = () => {
  const { dispatch } = useDispatch();
  const {
    messages,
    pageState: { path },
    theme
  } = useReduxState();

  const { data, loading, updateData } = useApiData<GetPathStepsMinimalRes>({
    url: `/admin/paths/${path.id}/steps`,
    onlyIf: Boolean(path)
  });

  const [administeredAchievements, setAdministeredAchievements] = useState([]);
  const [administeredCourses, setAdministeredCourses] = useState([]);
  const [administeredEvents, setAdministeredEvents] = useState([]);

  useEffect(() => {
    getAdministeredCourses();
    getAdministeredAchievements();
    getAdministeredCalendarEvents();
  }, []);

  const handleStepClick = (ev: React.MouseEvent) => {
    const as = ev.currentTarget.getAttribute('data-as');
    const href = ev.currentTarget.getAttribute('data-href');
    Router.push(href, as);
  };

  const getAdministeredCourses = async () => {
    try {
      const courses = await api.get(
        '/admin/courses-administered?permission=manage-users'
      );
      setAdministeredCourses(courses.data.courses);
    } catch (err) {
      await onError(err);
    }
  };

  const getAdministeredAchievements = async () => {
    try {
      const achievements = await api.get(
        '/admin/achievements?achievementType=' +
          ['conversation', 'external', 'milestone', 'tasklist'].join(
            filterValuesSeparator
          )
      );
      setAdministeredAchievements(achievements.data.achievements);
    } catch (err) {
      await onError(err);
    }
  };

  const getAdministeredCalendarEvents = async () => {
    try {
      const events = await api.get(
        '/admin/calendar-events?adminOnly=true&minimal=true'
      );
      setAdministeredEvents(events.data.events);
    } catch (err) {
      await onError(err);
    }
  };

  const onError = async (err: Error) => await dispatch.handleErrorDispatch(err);

  const existingAchievements = [];
  const existingCourses = [];
  const existingEvents = [];
  data?.steps?.forEach((step) => {
    for (const item of step.items) {
      if (item.type === 'achievement')
        existingAchievements.push(item.achievement.id);
      if (item.type === 'course') existingCourses.push(item.course.id);
      if (item.type === 'event') existingEvents.push(item.event.id);
    }
  });

  return (
    <Box
      headerContent={
        <>
          <PathStepsAddOrEdit
            availableAchievements={administeredAchievements}
            availableCourses={administeredCourses}
            availableEvents={administeredEvents}
            existingAchievements={existingAchievements}
            existingCourses={existingCourses}
            existingEvents={existingEvents}
            updateSteps={updateData}
          />
          <div style={{ display: 'inline-block', marginLeft: 10 }}>
            <PathStepsOrder steps={data?.steps} updateSteps={updateData} />
          </div>
        </>
      }
      loading={loading}
      padding={0}
      title={messages.pathsSteps}
    >
      {data?.steps?.length ? (
        data.steps.map((step, stepIndex) => {
          const achievementsInStep = [];
          const coursesInStep = [];
          const eventsInStep = [];
          for (const item of step.items) {
            if (item.type === 'achievement')
              achievementsInStep.push(item.achievement.id);
            if (item.type === 'course') coursesInStep.push(item.course.id);
            if (item.type === 'event') eventsInStep.push(item.event.id);
          }

          return (
            <BoxRow key={step.id}>
              <div className="step">
                <div className="step-summary">
                  <PathStepSummary
                    step={step}
                    stepLinkHref={`/admin/paths/${path.id}/steps/${step.id}`}
                  />
                </div>

                <div className="step-actions">
                  <PathStepsAddOrEdit
                    availableAchievements={administeredAchievements}
                    availableCourses={administeredCourses}
                    availableEvents={administeredEvents}
                    existingAchievements={existingAchievements.filter(
                      (id) => !achievementsInStep.includes(id)
                    )}
                    existingCourses={existingCourses.filter(
                      (id) => !coursesInStep.includes(id)
                    )}
                    existingEvents={existingEvents.filter(
                      (id) => !eventsInStep.includes(id)
                    )}
                    step={step}
                    updateSteps={updateData}
                  />

                  <MoreOptions category="lighter">
                    <PathStepsRemove
                      stepId={step.id}
                      updateSteps={updateData}
                    />
                  </MoreOptions>
                </div>
              </div>
            </BoxRow>
          );
        })
      ) : (
        <EmptyIndicator
          description={messages.pathsStepsEmptyDescription}
          icon="book"
          title={messages.pathsStepsEmpty}
        />
      )}

      <style jsx>{`
        .step {
          display: flex;
          flex-wrap: wrap;
          gap: 25px;
          padding: 5px 0;
        }
        .step-summary {
          flex-grow: 1;
          flex-shrink: 1;
        }
        .step-actions {
          display: flex;
          flex-grow: 0;
          flex-shrink: 0;
          gap: 10px;
        }
      `}</style>
    </Box>
  );
};

export default PathSteps;
